import React from 'react';
import SEO from '../components/SEO';
import aboutTeam from '../assets/images/about-team.jpg';
import potturuSwapna from '../assets/images/potturu_swapna.png';

function About() {
  return (
    <div>
      <SEO 
        title="About Us - Industry Leaders in AI & Digital Transformation"
        description="Founded by Potturu Swapna, Vithminds leverages decades of IT expertise to deliver AI-first strategies, Agentic AI, and intelligent automation for Fortune 500 enterprises."
        keywords="about vithminds, Potturu Swapna, AI consulting, digital transformation experts, enterprise AI solutions"
      />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>About Vithminds</h1>
          <p>
            Empowering enterprises to transition from traditional systems to 
            AI-powered, self-evolving platforms
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section">
        <h2 className="section-title">Our Mission</h2>
        <p className="section-subtitle">
          To enable enterprises to transition from traditional systems to AI-powered, 
          self-evolving platforms that drive efficiency, innovation, and sustained competitive advantage.
        </p>
      </section>

      {/* Who We Are */}
      <section className="section section-light">
        <h2 className="section-title">Who We Are</h2>
        <div className="grid-2" style={{ marginTop: '3rem', gap: '3rem', alignItems: 'center' }}>
          <div>
            <img 
              src={aboutTeam} 
              alt="AI Innovation and Digital Transformation" 
              className="feature-image shine-effect"
            />
          </div>
          <div>
            <p className="premium-text">
              Vithminds is built by industry leaders with decades of IT expertise, 
              backed by trusted advisors across AI, Low-Code/No-Code, Data, and 
              Digital Ecosystem Services.
            </p>
            <p className="premium-text" style={{ marginTop: '1.5rem' }}>
              Our team has a strong track record in incubating and scaling digital services, 
              driving large-scale transformation programs, and delivering measurable value 
              for global enterprises.
            </p>
            <p className="premium-text" style={{ marginTop: '1.5rem' }}>
              With deep CXO-level relationships and a powerful partner ecosystem, 
              we are uniquely positioned to accelerate transformation at scale and speed.
            </p>
            <p className="premium-text" style={{ marginTop: '1.5rem' }}>
              Founded and led by Potturu Swapna, Vithminds AI leverages deep global 
              experience in leading large-scale digital transformation initiatives 
              for Fortune 500 enterprises.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section">
        <h2 className="section-title">Leadership</h2>
        <div style={{ maxWidth: '800px', margin: '3rem auto', textAlign: 'center' }}>
          <div className="card">
            <div className="leadership-image-container">
              <img 
                src={potturuSwapna} 
                alt="Potturu Swapna - Founder & CEO"
                className="leadership-image"
              />
            </div>
            <h3 className="card-title">Potturu Swapna</h3>
            <p style={{ color: 'var(--primary-color)', fontWeight: '600', marginBottom: '1rem' }}>
              Founder & CEO
            </p>
            <p className="card-text">
              Deep global experience in leading large-scale digital transformation 
              initiatives for Fortune 500 enterprises. Specializes in AI-first strategies, 
              Agentic AI, intelligent automation, and data-driven insights to modernize 
              legacy ecosystems and unlock new sources of growth.
            </p>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="section section-dark">
        <h2 className="section-title">Our Expertise</h2>
        <p className="section-subtitle">
          Comprehensive capabilities across the AI and digital transformation spectrum
        </p>
        <div className="grid-3">
          <div className="card glass-card shine-effect">
            <div className="card-icon">🤖</div>
            <h3 className="card-title" style={{ color: 'white' }}>AI & Agentic AI</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Advanced AI solutions including autonomous agents, machine learning, 
              and cognitive automation for enterprise-scale deployments.
            </p>
          </div>
          <div className="card glass-card shine-effect">
            <div className="card-icon">🔧</div>
            <h3 className="card-title" style={{ color: 'white' }}>Low-Code/No-Code</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Rapid application development using modern platforms that accelerate 
              time-to-market and reduce development costs.
            </p>
          </div>
          <div className="card glass-card shine-effect">
            <div className="card-icon">📊</div>
            <h3 className="card-title" style={{ color: 'white' }}>Data Services</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              End-to-end data solutions including analytics, data engineering, 
              and business intelligence for actionable insights.
            </p>
          </div>
          <div className="card glass-card shine-effect">
            <div className="card-icon">🌐</div>
            <h3 className="card-title" style={{ color: 'white' }}>Digital Ecosystem</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Building interconnected digital ecosystems that enable seamless 
              integration across enterprise systems and platforms.
            </p>
          </div>
          <div className="card glass-card shine-effect">
            <div className="card-icon">💼</div>
            <h3 className="card-title" style={{ color: 'white' }}>Consulting Services</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Strategic consulting with CXO-level expertise to guide your 
              digital transformation journey from strategy to execution.
            </p>
          </div>
          <div className="card glass-card shine-effect">
            <div className="card-icon">🚀</div>
            <h3 className="card-title" style={{ color: 'white' }}>Software Development</h3>
            <p className="card-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Custom software development services leveraging modern technologies 
              and agile methodologies for scalable solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section">
        <h2 className="section-title">Our Approach</h2>
        <div className="grid-2" style={{ marginTop: '3rem' }}>
          <div className="card">
            <div className="card-icon">🎯</div>
            <h3 className="card-title">AI-First Strategy</h3>
            <p className="card-text">
              We lead with AI-first thinking, ensuring every solution leverages 
              the latest AI capabilities to maximize business value and competitive advantage.
            </p>
          </div>
          <div className="card">
            <div className="card-icon">⚡</div>
            <h3 className="card-title">Rapid Time-to-Value</h3>
            <p className="card-text">
              Our proven methodologies ensure quick wins and measurable results, 
              delivering value early and often throughout the transformation journey.
            </p>
          </div>
          <div className="card">
            <div className="card-icon">🤝</div>
            <h3 className="card-title">Partnership Approach</h3>
            <p className="card-text">
              We work as true partners, combining our expertise with your domain 
              knowledge to create tailored solutions that fit your unique needs.
            </p>
          </div>
          <div className="card">
            <div className="card-icon">📈</div>
            <h3 className="card-title">Scalable Solutions</h3>
            <p className="card-text">
              Every solution is designed for enterprise scale, ensuring it can grow 
              with your business and adapt to changing requirements.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-light">
        <div style={{ textAlign: 'center' }}>
          <h2 className="section-title">Let's Build the Future Together</h2>
          <p className="section-subtitle">
            Contact us to learn how Vithminds can help transform your enterprise
          </p>
          <a href="/contact-us" className="cta-button" aria-label="Contact Vithminds for consultation">Get in Touch</a>
        </div>
      </section>
    </div>
  );
}

export default About;